import React from 'react';
import { StyleSheet, Text, View } from '@react-pdf/renderer';

import dayjs, { dateDisplayFormat } from '~libs/dayjs';

import Table from '~components/pdf/Table';

import { Section } from '~support-at-home/models';

import SectionTotal from './SectionTotal';

interface ComponentProps {
  section: Section;
}

const styles = StyleSheet.create({
  section: {
    border: '1px solid #949494',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    marginBottom: 1,
    position: 'relative',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 8,
    width: '100%',
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  headerInfo: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  categoryName: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  coContrib: {
    fontSize: 10,
    color: '#666',
  },
  sectionTotalsWrapper: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    width: '100%',
  },
  sectionTotalsBox: {
    width: '50%',
    minWidth: 160,
    fontSize: 8,
    marginRight: 0,
  },
  emptyState: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 8,
    width: '100%',
    textAlign: 'center',
  },
});

const columns = [
  { title: 'Service Type', dataKey: 'service_type', width: '16%', align: 'left' as const },
  { title: 'Start Date', dataKey: 'start_date', width: '9%', align: 'center' as const },
  { title: 'End Date', dataKey: 'end_date', width: '9%', align: 'center' as const },
  { title: 'Shift', dataKey: 'shift', width: '8%', align: 'center' as const },
  { title: 'Weekdays', dataKey: 'weekdays', width: '9%', align: 'center' as const },
  { title: 'Freq.', dataKey: 'frequency', width: '7%', align: 'center' as const },
  { title: 'Hrs / Qty', dataKey: 'quantity', width: '6%', align: 'center' as const },
  {
    title: 'Ext. Reference',
    dataKey: 'external_reference',
    width: '13%',
    align: 'center' as const,
  },
  { title: 'Public Holidays', dataKey: 'include_public_holidays', width: '8%', align: 'center' as const },
  { title: 'Rate', dataKey: 'unit_amount', width: '7%', align: 'right' as const },
  { title: 'Total', dataKey: 'total_amount', width: '8%', align: 'right' as const },
];

const BudgetSection: React.FC<ComponentProps> = ({ section }) => {
  const { category } = section;
  const { items } = section;

  const setupTableData = () => {
    return items.map((item) => {
      return {
        service_type: item.service_type?.name || '',
        start_date: dayjs(item.start_date).format(dateDisplayFormat),
        end_date: dayjs(item.end_date).format(dateDisplayFormat),
        shift: item.shift?.name || '',
        weekdays: item.week_days_text || '',
        frequency: item.frequency?.name || '',
        quantity: item.quantity,
        external_reference: item.external_reference || '',
        include_public_holidays: item.include_public_holidays ? 'Yes' : 'No',
        unit_amount: `$${item.unit_amount?.toFixed(2) || '0.00'}`,
        total_amount: `$${item.total_amount?.toFixed(2) || '0.00'}`,
      };
    });
  };

  const tableData = setupTableData();

  return (
    <>
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.headerInfo}>
            <Text style={styles.categoryName}>{category?.name}</Text>
            <Text style={styles.coContrib}>Co-Contribution: {section.contrib_proportion ?? 0}%</Text>
          </View>
        </View>
        {tableData.length > 0 ? (
          <Table columns={columns} data={tableData} alternateRows={true} />
        ) : (
          <Text style={styles.emptyState}>No items found</Text>
        )}
      </View>
      <View style={styles.sectionTotalsWrapper}>
        <View style={styles.sectionTotalsBox}>
          <SectionTotal category_id={section.category_id} section={section} />
        </View>
      </View>
    </>
  );
};

export default BudgetSection;
